import { yupResolver } from '@hookform/resolvers/yup';
import { CustomPageType, FooterCustomization, IOperationResultNoValue, IRichContentEditorState } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FieldError, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { createRichContentEditorStateWithText } from '../../../../../models/UiUtils';
import { useDeleteBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useDeleteBlogStyleMutation';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { Button } from '../../../../../web/components-v2/Button';
import { ColorPicker } from '../../../../../web/components-v2/ColorPicker';
import { ConfirmationDialog, DefaultConfirmationOptions } from '../../../../../web/components/ConfirmationDialog';
import { RichContentDocumentEditor } from '../../../../../web/components/richContent/RichContentDocumentEditor';
import { PlusIcon } from '../../../../../web/components/svgs/icons/PlusIcon';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { charmGray } from '../../../../../web/styles/colors';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs/AccountContext';
import { styleSheet } from '../../../../components/blogs/styles';
import { LabeledInput, labeledInputStyles } from '../../../../components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../../components/Layout';
import { applyPresetToFooterForm } from '../applyPreset';
import { StylePreset } from '../presets';
import {
	DEFAULT_FOOTER_BACKEND_TEXT_VALUES,
	DEFAULT_FOOTER_FORM_VALUES,
	DEFAULT_SIMPLE_LINK_VALUES,
	isDefaultFooterText,
	MINIMUM_FOOTER_LINKS,
} from './constants';
import { FeatureToggleSection } from './FeatureToggleSection';
import { footerSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface FooterDesignFormProps {
	customContent?: FooterCustomization;
	onFormDirty: () => void;
	onUpdateSuccess: () => void;
}

export interface FooterDesignFormRef {
	applyPreset: (preset: StylePreset) => void;
}

export const FooterDesignForm = forwardRef<FooterDesignFormRef, FooterDesignFormProps>(
	({ customContent, onFormDirty, onUpdateSuccess }, ref) => {
		const errorMessages = useErrorMessages();
		const { account, accountId, impersonationContext } = useAccountContext();
		const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

		// Determine which features are available for this account
		const blogFeature = account?.features?.blogFeature;
		const blogFeatureAvailable = !!blogFeature?.enabled && !blogFeature?.dudaSiteId;
		const surveyFeatureAvailable = !!account?.features?.surveys?.enabled;
		const meetingFeatureAvailable = !!account?.features?.meetingScheduler?.enabled;

		const form = useForm<FooterCustomization>({
			defaultValues: DEFAULT_FOOTER_FORM_VALUES,
			mode: 'onBlur',
			resolver: yupResolver(footerSchema),
		});

		const {
			control,
			formState: { errors, isDirty },
			getValues,
			handleSubmit,
			register,
			setValue,
			watch,
		} = form;

		// Watch color values for live updating
		const backgroundColor = watch('backgroundColor');
		const textColor = watch('textColor');
		const linkColor = watch('linkColor');

		// Watch enabledFor for feature toggles
		const enabledFor = watch('enabledFor');

		const handleEnabledForChange = useCallback(
			(newEnabledFor: CustomPageType[]) => {
				setValue('enabledFor', newEnabledFor, { shouldDirty: true });
			},
			[setValue]
		);

		const { append, fields } = useFieldArray({ control, name: 'links' });

		const [footerRichContentState, setFooterRichContentState] = useState<IRichContentEditorState>(
			createRichContentEditorStateWithText('')
		);
		const handleTextChange = (content: IRichContentEditorState) => {
			setValue('text', content.getRawRichTextContent());
		};

		const handleAddLink = (event: React.MouseEvent<HTMLElement>) => {
			event.preventDefault();
			event.stopPropagation();

			append(DEFAULT_SIMPLE_LINK_VALUES);
		};

		const updateFooterMutation = useFindOrCreateAndUpdateBlogStyleMutation({
			onSuccess: () => {
				onUpdateSuccess();
				toast.success('Saved changes successfully');
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			},
		});

		const deleteFooterMutation = useDeleteBlogStyleMutation({
			onSuccess: () => {
				form.reset(DEFAULT_FOOTER_FORM_VALUES);
				setFooterRichContentState(createRichContentEditorStateWithText(''));
				onUpdateSuccess();
				toast.success('Customization deleted successfully');
				setShowDeleteDialog(false);
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
				setShowDeleteDialog(false);
			},
		});

		const onSubmit: SubmitHandler<FooterCustomization> = (data, event) => {
			event.preventDefault();
			event.stopPropagation();

			updateFooterMutation.mutate({
				id: accountId,
				impersonationContext,
				style: data,
			});
		};

		const handleDeleteCustomization = () => {
			setShowDeleteDialog(true);
		};

		const handleDeleteConfirm = (_: never, canceled: boolean) => {
			if (!canceled && customContent && customContent.id) {
				deleteFooterMutation.mutate({
					id: customContent.id,
					impersonationContext,
				});
			} else {
				setShowDeleteDialog(false);
			}
		};

		useEffect(() => {
			if (customContent && !equal(customContent, getValues())) {
				Object.entries(customContent).forEach(entry => {
					if (entry[0] === 'links' && entry[1].length <= 0) {
						return;
					}
					if (entry[0] === 'links' && entry[1].length < MINIMUM_FOOTER_LINKS) {
						// links array dynamically generates ui rows
						// minimum of 3 maintains spec
						const newLinksArray = [...entry[1]];
						newLinksArray.length = MINIMUM_FOOTER_LINKS;
						newLinksArray.fill(DEFAULT_SIMPLE_LINK_VALUES, entry[1].length);
						setValue('links', newLinksArray);
						return;
					}
					if (entry[0] === 'text') {
						const documentText = entry[1].document;
						if (documentText && !isDefaultFooterText(documentText)) {
							const richContentState = createRichContentEditorStateWithText(documentText);
							setFooterRichContentState(richContentState);
							setValue('text', entry[1]);
						}
						return;
					}
					if (entry[0] === 'copyrightInfo') {
						if (entry[1] && entry[1] !== DEFAULT_FOOTER_BACKEND_TEXT_VALUES.copyrightInfo) {
							setValue('copyrightInfo', entry[1]);
						}
						return;
					}
					if (entry[1] !== undefined && entry[1] !== null) {
						setValue(entry[0] as Extract<keyof FooterCustomization, string>, entry[1]);
					}
				});
			}
		}, [customContent, getValues, setValue]);

		useEffect(() => {
			if (isDirty) {
				onFormDirty();
			}
		}, [isDirty, onFormDirty]);

		useImperativeHandle(ref, () => ({
			applyPreset: (preset: StylePreset) => {
				const currentValues = getValues();
				const updatedValues = applyPresetToFooterForm(preset, currentValues);

				Object.entries(updatedValues).forEach(([key, value]) => {
					if (value !== undefined) {
						setValue(key as keyof FooterCustomization, value, { shouldDirty: true });
					}
				});
			},
		}));

		return (
			<>
				<FormProvider {...form}>
					<TemplateFormWrapper
						title='Customize Footer'
						infoMessage='This styling applies to blogs, surveys, and meetings.'
						isLoading={updateFooterMutation.isPending}
						errors={errors}
						onSubmit={handleSubmit(onSubmit)}
						onDeleteCustomization={handleDeleteCustomization}
						isDeleting={deleteFooterMutation.isPending}
						hasCustomization={!!customContent && !!customContent.id}
					>
						<Row className={css(styleSheet.gap24)}>
							<Column className={css(bs.flexhalf, styleSheet.gap24)}>
								<h2 className={css(styleSheet.subtitleText)}>Colors</h2>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Background Color</label>
									<ColorPicker
										value={backgroundColor || '#ffffff'}
										onChange={color => setValue('backgroundColor', color)}
									/>
								</div>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Text Color</label>
									<ColorPicker value={textColor || '#000000'} onChange={color => setValue('textColor', color)} />
								</div>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Link Color</label>
									<ColorPicker value={linkColor || '#0066cc'} onChange={color => setValue('linkColor', color)} />
								</div>
							</Column>
							<Column className={css(bs.flex2, styleSheet.gap24)}>
								<h2 className={css(styleSheet.subtitleText)}>Information</h2>
								<Column>
									<label className={css(labeledInputStyles.label, labeledInputStyles.labelRow)}>Footer Text</label>
									<RichContentDocumentEditor
										className={css(styleSheet.editor)}
										config={{
											autoresizeToFitContent: true,
											plugins: 'autolink',
											toolbar: 'bold italic underline',
										}}
										contentPlaceholderText={DEFAULT_FOOTER_BACKEND_TEXT_VALUES.footerText}
										contentState={footerRichContentState}
										disableFileAttachment
										onContentStateChanged={handleTextChange}
									/>
								</Column>
								<LabeledInput
									label='Copyright Info'
									placeholder={DEFAULT_FOOTER_BACKEND_TEXT_VALUES.copyrightInfo}
									{...register('copyrightInfo')}
								/>
							</Column>
							<Column className={css(bs.flex1, styleSheet.gap24)}>
								<h2 className={css(styleSheet.subtitleText)}>Links</h2>
								{fields.map((field, index) => (
									<Row className={css(bs.itemsEnd, styleSheet.gap12)} key={field.id}>
										<LabeledInput
											label={`Link ${index + 1}`}
											placeholder='Link label'
											{...register(`links.${index}.label`)}
										/>
										<LabeledInput
											error={errors?.links?.[index] as FieldError}
											placeholder='URL'
											{...register(`links.${index}.destination`)}
										/>
									</Row>
								))}
								<Button variant='secondary' onClick={handleAddLink}>
									<PlusIcon fillColor={charmGray} /> Add Link
								</Button>
							</Column>
						</Row>
						<FeatureToggleSection
							enabledFor={enabledFor}
							onEnabledForChange={handleEnabledForChange}
							blogFeatureAvailable={blogFeatureAvailable}
							surveyFeatureAvailable={surveyFeatureAvailable}
							meetingFeatureAvailable={meetingFeatureAvailable}
							disabled={updateFooterMutation.isPending}
						/>
					</TemplateFormWrapper>
				</FormProvider>
				{showDeleteDialog && (
					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{ isOpen: showDeleteDialog, onRequestClose: handleDeleteConfirm }}
						options={DefaultConfirmationOptions}
						title='Are you sure you want to delete this customization? This action cannot be undone.'
					/>
				)}
			</>
		);
	}
);

FooterDesignForm.displayName = 'FooterDesignForm';
